import { useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { openConsultationModal } from "../components/ConsultationModal";
import {
  Brain, ShieldCheck, Rocket, Terminal, CreditCard, ScanFace,
  Video, BarChart3, Briefcase, ArrowRight, ExternalLink,
  Zap, Lock, Server, CheckCircle2, ChevronRight, Activity,
  Layers, HardDrive, Shield, Users, GitCommit, Database, Check,
  Sparkles, RefreshCw, Cpu, Gauge, FileText, AlertTriangle,
  Cloud, Globe, DollarSign, Search, Bell, Settings, TrendingUp,
  MessageSquare, Radio, Network, Play, Key, Laptop, Code2,
  FolderLock, UserCheck, Clock, Award, AlertCircle,
  Calendar, GraduationCap, Headphones, X, IndianRupee
} from "lucide-react";

export default function Norbit() {
  // Navigation & Interactive states
  const [activePillar, setActivePillar] = useState(0);
  const [activeWorkflow, setActiveWorkflow] = useState("sales");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "nORBIT",
    "alternateName": "Neuravolt Operational & Resource Business Intelligence Technology",
    "headline": "Autonomous Enterprise Operations Platform",
    "description": "Your Entire Business. One Autonomous Operating System. Sovereign all-in-one enterprise OS bringing together Guardian SRE, Business & Revenue, Multi-Tenant Client Portal, Launchpad AI Talent Engine, and HRMS BioAuth.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Linux, Cloud, Bare Metal",
    "provider": {
      "@type": "Organization",
      "name": "Neuravolt",
      "legalName": "VOLTNOVATE SOLUTIONS LLP",
      "url": "https://neuravolt.in",
    },
    "offers": {
      "@type": "Offer",
      "price": "Custom",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
    },
    "featureList": [
      "NeuraVolt Guardian Autonomous SRE & 2-Second Active Containment",
      "Business & Revenue Engine with Payment Gateway (HMAC-SHA256)",
      "Multi-Tenant White-Label Client Portal",
      "NeuraVolt Launchpad AI Talent & Docker Sandboxed Verification",
      "HRMS & BioAuth 128-D Webcam Biometric Attendance",
      "MeetPass Secure Meeting Gateways",
      "NestJS 37-Module Monolith with PostgreSQL 273 Models",
      "Zero SaaS Sprawl Sovereign Architecture",
    ],
  };

  // ──────────────────────────────────────────────────────────────────────────
  // THE 5 TRUE nORBIT PILLARS DATA
  // ──────────────────────────────────────────────────────────────────────────
  const pillars = [
    {
      id: "guardian",
      num: "01",
      name: "NeuraVolt Guardian",
      badge: "The Shield of nORBIT",
      category: "Autonomous SRE & Cyber Defense",
      tagline: "2-second webshell containment and autonomous infrastructure self-healing.",
      icon: ShieldCheck,
      accent: "blue",
      accentBg: "bg-blue-500/10",
      accentBorder: "border-blue-500/20",
      accentText: "text-blue-600",
      summary: "Guardian is the autonomous cyber shield and SRE engine of nORBIT. It monitors live servers, isolates threats in sub-2 seconds, and executes self-healing runbooks without third-party agent sprawl.",
      metrics: [
        { label: "Active Containment", value: "< 2s", sub: "Webshell & malware quarantine" },
        { label: "Governance Auth", value: "2-Step", sub: "Bcrypt Super Admin + CONFIRM" },
        { label: "Notification Hub", value: "In-House", sub: "Telegram, VAPID Push & WhatsApp" },
      ],
      features: [
        {
          title: "Active Containment & Quarantine",
          desc: "Sub-2-second automatic isolation of unauthorized rootkit modifications, reverse shells, and malicious uploads with zero false positives."
        },
        {
          title: "2-Step Security Governance",
          desc: "Destructive server operations (disk write, process termination, database flushing) require Bcrypt Super Admin auth followed by cryptographic CONFIRM prompts."
        },
        {
          title: "Dynamic SRE Engine",
          desc: "Real-time process attribution via /proc, live disk inspection, rogue backup discovery, interactive web terminal, df -h, ps aux, and docker ps telemetry."
        },
        {
          title: "Zero-Dependency Notification Hub",
          desc: "Built-in Telegram bots, native browser VAPID push notifications, and WhatsApp enterprise gateway. Zero reliance on expensive external SMS providers."
        },
        {
          title: "Dual Fleet Transport",
          desc: "Direct local/SSH execution for managed internal VPS nodes alongside bidirectional WebSocket agents for client-owned BYOVPS deployments."
        }
      ],
      livePreview: {
        title: "Guardian Live Shield Engine",
        status: "Armored · Telemetry Active",
        command: "guardian-daemon --inspect --quarantine-auto",
        logs: [
          "[SYS] Kernel eBPF probe active on /proc/kcore",
          "[SEC] Inode 481920 written to /var/www/uploads/shell.php",
          "[WARN] Heuristic signature matched: Eval-Obfuscated Webshell",
          "[ACTION] Containment executed in 1.42s (chmod 000 + chattr +i)",
          "[NOTIFY] Incident alert dispatched via Telegram & VAPID Push"
        ]
      }
    },
    {
      id: "business",
      num: "02",
      name: "CRM & Revenue Engine",
      badge: "CRM & Financial Engine",
      category: "Enterprise CRM + ERP + Billing + Payment Gateway",
      tagline: "End-to-end client lifecycle from CRM lead capture to automated GST invoice and bank deposit.",
      icon: CreditCard,
      accent: "amber",
      accentBg: "bg-amber-500/10",
      accentBorder: "border-amber-500/20",
      accentText: "text-amber-600",
      summary: "The financial backbone that interconnects client sales pipelines, dynamic quotation generation, single-sink payment gateway, AMC license renewal sentinels, and double-entry ledger.",
      metrics: [
        { label: "PG Verification", value: "HMAC-SHA256", sub: "Timing-safe webhook audit" },
        { label: "Tax Compliance", value: "Automated GST", sub: "Dynamic e-invoicing & PDF gen" },
        { label: "Asset Sentinels", value: "100% Tracked", sub: "Domain, SSL & AMC renewals" },
      ],
      features: [
        {
          title: "Sales & Leads Pipeline",
          desc: "Complete deal lifecycle tracking, conversion stages, automated proposal builder, and Git-versioned client quotations with e-sign verification."
        },
        {
          title: "GST-Compliant Billing & Invoicing",
          desc: "Automated tax calculation, HSN code attribution, automated late payment reminders, and server-side PDF invoice compilation with QR payment links."
        },
        {
          title: "Payment Gateway & Payment Proof Workflows",
          desc: "Direct single-sink payment processing with HMAC-SHA256 timing-safe webhook verification, alongside bank transfer proof upload and manual approval queues."
        },
        {
          title: "AMC & Infrastructure Asset Sentinel",
          desc: "Continuous health and renewal tracking for client servers, domains, SSL certificates, and annual maintenance contracts with automated expiration alerts."
        },
        {
          title: "Double-Entry Accounting Ledger",
          desc: "Real-time sync between incoming payments, project milestone completions, vendor expenses, and automated P&L statement generation."
        }
      ],
      livePreview: {
        title: "Payment Gateway Webhook Sentinel",
        status: "Payment Sink Synced · Ledger Active",
        command: "POST /api/v1/billing/webhooks/gateway",
        logs: [
          "[INVOICE] #NV-2026-089 generated for Enterprise License",
          "[PAYMENT] Payment Gateway webhook received: order_9481903",
          "[VERIFY] HMAC-SHA256 signature verified in 4ms",
          "[LEDGER] Credited ₹3,45,000 to Operating Account (GST 18% partitioned)",
          "[DISPATCH] Stamped receipt emailed & client portal unlocked"
        ]
      }
    },
    {
      id: "client-portal",
      num: "03",
      name: "Client Portal",
      badge: "Self-Service Portal",
      category: "Multi-Tenant White-Label Client Portal",
      tagline: "Dedicated self-service interface for every client with zero internal system exposure.",
      icon: Laptop,
      accent: "cyan",
      accentBg: "bg-cyan-500/10",
      accentBorder: "border-cyan-500/20",
      accentText: "text-cyan-600",
      summary: "Dedicated application (apps/client-portal) giving each enterprise client their own isolated workspace to view deliverables, pay invoices, track SLA support tickets, and review website health.",
      metrics: [
        { label: "Isolation", value: "Strict Tenant", sub: "Zero cross-client data leak" },
        { label: "Invoice Pay", value: "1-Click", sub: "Direct Payment Gateway link" },
        { label: "SLA Response", value: "< 15 Mins", sub: "Priority ticketing dispatch" },
      ],
      features: [
        {
          title: "Multi-Tenant Data Isolation",
          desc: "PostgreSQL row-level isolation ensuring clients only ever access their own project repositories, server health charts, and billing history."
        },
        {
          title: "1-Click Online Invoice Settlement",
          desc: "Clients can review itemized milestones, download GST invoices, and settle balances instantly via UPI, NetBanking, and corporate credit cards."
        },
        {
          title: "SLA-Guaranteed Support Ticketing",
          desc: "Direct issue dispatch to assigned engineering leads with live milestone status, SLA countdown timers, and resolution sign-offs."
        },
        {
          title: "Live Website & App Health Telemetry",
          desc: "Real-time uptime metrics, SSL certificate status, response latency graphs, and scheduled maintenance windows surfaced in plain business English."
        },
        {
          title: "White-Label Pass-Through",
          desc: "Custom client subdomains, brand logos, and secure authentication tokens that shield your internal nORBIT master admin operations."
        }
      ],
      livePreview: {
        title: "apps/client-portal Tenant Gateway",
        status: "Tenant: Acme Corp · Secure Session",
        command: "client-session-auth --tenant-id 8192",
        logs: [
          "[PORTAL] Authenticated client_admin@acmecorp.com",
          "[METRICS] Project: Enterprise Mobile Core v2.4 (94% Complete)",
          "[HEALTH] Live Prod Node: 99.99% Uptime (Latency: 28ms)",
          "[TICKET] #TK-402: 'Staging DB migration' marked Resolved",
          "[INVOICE] Next Retainer due in 12 days (₹1,50,000)"
        ]
      }
    },
    {
      id: "launchpad",
      num: "04",
      name: "NeuraVolt Launchpad",
      badge: "The Talent Engine",
      category: "AI Talent & Developer Verification Engine",
      tagline: "Autonomous candidate evaluation via algorithmic AI challenges and Docker sandboxes.",
      icon: Brain,
      accent: "purple",
      accentBg: "bg-purple-500/10",
      accentBorder: "border-purple-500/20",
      accentText: "text-purple-600",
      summary: "NeuraVolt Launchpad is the developer competency and EdTech engine of nORBIT. It trains, evaluates, and verifies developers through 3 dedicated portals, AI-generated coding challenges, and ephemeral container execution.",
      metrics: [
        { label: "Dedicated Portals", value: "3 Dedicated", sub: "Student, Admin & Partner" },
        { label: "Sandbox Security", value: "Isolated Docker", sub: "Zero state bleed or escape" },
        { label: "Verification", value: "Algorithmic", sub: "AST & Big-O complexity audits" },
      ],
      features: [
        {
          title: "3 Dedicated Operating Portals",
          desc: "Custom web interfaces purpose-built for Students (challenges & portfolios), Launchpad Admins (rubrics & cohorts), and Partner Colleges (placement analytics)."
        },
        {
          title: "AI Challenge Generation",
          desc: "Dynamically synthesizes unique algorithmic problems and full-stack project scenarios, preventing plagiarism and outdated static test banks."
        },
        {
          title: "Automated Sandbox Code Verification",
          desc: "Pulls student GitHub repositories directly into ephemeral, isolated Docker runner containers to compile, execute unit tests, and measure runtime performance."
        },
        {
          title: "Career DNA & Skill Passport",
          desc: "Calculates an immutable Skill Score, Trust Score, and Points Wallet based on real code performance, verified commits, and architectural complexity."
        },
        {
          title: "Live Leaderboards & Hiring Pipeline",
          desc: "Surfaces top verified engineering talent directly to internal project teams or partner enterprises with zero resume screening bias."
        }
      ],
      livePreview: {
        title: "Launchpad Sandbox Verification",
        status: "Docker Runner #482 · AST Auditing",
        command: "launchpad-eval --repo candidate/fullstack-api",
        logs: [
          "[DOCKER] Spun up isolated ephemeral container runner-c92",
          "[GIT] Pulled commit 4c89f2a from candidate fork",
          "[AST] Analyzing code syntax tree & Big-O complexity",
          "[TEST] 42/42 Unit Tests Passed (Memory: 34MB, CPU: 12ms)",
          "[PASSPORT] Skill Score updated: 894/1000 · Trust Tier: A+"
        ]
      }
    },
    {
      id: "hrms",
      num: "05",
      name: "Project Management & HRMS",
      badge: "Projects & Workforce",
      category: "Agile Projects + HRMS + Biometric Identity",
      tagline: "Agile project sprint tracking, task kanban boards, in-house facial biometric attendance, and PIN-secured meetings.",
      icon: Users,
      accent: "emerald",
      accentBg: "bg-emerald-500/10",
      accentBorder: "border-emerald-500/20",
      accentText: "text-emerald-600",
      summary: "The workforce management engine combining in-house 128-D facial biometric attendance, fine-grained RBAC permissions, sprint task tracking, and MeetPass secure video gateways.",
      metrics: [
        { label: "BioAuth Engine", value: "128-D AI Vector", sub: "Webcam biometric match" },
        { label: "Data Privacy", value: "100% DPDP-2023", sub: "Vectors never leave local DB" },
        { label: "Meeting Gateway", value: "MeetPass PIN", sub: "Anti-eavesdrop access barrier" },
      ],
      features: [
        {
          title: "NeuraVolt BioAuth Facial Attendance",
          desc: "Browser webcam generates a 128-dimensional facial vector to verify employee identity in milliseconds. Raw photos are discarded; only mathematical vectors are matched."
        },
        {
          title: "Strict DPDP-2023 Sovereign Compliance",
          desc: "All biometric vectors remain inside your private PostgreSQL database. Zero third-party cloud AI APIs ever touch or store your team's sensitive biometric data."
        },
        {
          title: "Dynamic RBAC & @Permissions",
          desc: "Role-Based Access Control spanning Super Admin, Admin, Manager, Developer, QA, and Employee with granular method-level permission decorators."
        },
        {
          title: "Sprint Management & Task Logs",
          desc: "Integrated task assignment, engineering time logging, sprint burndown charts, and automated zero-touch monthly payroll calculations."
        },
        {
          title: "NeuraVolt MeetPass Collaboration",
          desc: "PIN-secured conference gateway that verifies participant credentials and generates encrypted meeting links without third-party video logging."
        }
      ],
      livePreview: {
        title: "BioAuth 128-D Vector Matcher",
        status: "Face Vector Matched · Attendance Logged",
        command: "bioauth-verify --vector-check --dpdp-compliant",
        logs: [
          "[WEBCAM] Captured facial mesh from browser video feed",
          "[VECTOR] Generated 128-dimensional embedding [0.048, -0.192, ...]",
          "[EUCLIDEAN] Distance score: 0.184 (Confidence: 99.4%)",
          "[RECORD] Attendance logged for Emp #NV-042 at 09:31:04 AM",
          "[SEC] Raw image buffer flushed from memory; zero photo stored"
        ]
      }
    }
  ];

  // ──────────────────────────────────────────────────────────────────────────
  // 06 — AUTONOMOUS WORKFLOWS DATA
  // ──────────────────────────────────────────────────────────────────────────
  const workflows = [
    {
      id: "sales",
      name: "Business & Revenue Flow",
      tagline: "From Lead Ingestion to Automated Ledger Entry",
      trigger: "Client Approves Digital Quotation",
      stages: [
        { label: "Sales Lead", engine: "Pipeline CRM", desc: "Lead qualified & requirements mapped", icon: Briefcase, color: "text-amber-600", bg: "bg-amber-50" },
        { label: "Proposal", engine: "Proposal Engine", desc: "Dynamic versioned scope compiled", icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Invoice Gen", engine: "Billing Engine", desc: "Automated GST invoice & payment link", icon: Database, color: "text-purple-600", bg: "bg-purple-50" },
        { label: "Payment Sink", engine: "Payment Gateway", desc: "HMAC-SHA256 verified bank deposit", icon: CreditCard, color: "text-emerald-600", bg: "bg-emerald-50" },
        { label: "Ledger Sync", engine: "Ledger", desc: "Double-entry P&L auto reconciliation", icon: CheckCircle2, color: "text-cyan-600", bg: "bg-cyan-50" },
      ]
    },
    {
      id: "client",
      name: "Client Portal Flow",
      tagline: "Self-Service Milestone Review & Ticketing",
      trigger: "Client Raises Priority Request in Portal",
      stages: [
        { label: "Client Login", engine: "apps/client-portal", desc: "Isolated tenant authentication", icon: Laptop, color: "text-cyan-600", bg: "bg-cyan-50" },
        { label: "Deliverable", engine: "Project Mesh", desc: "Review live staging & sprint commits", icon: Layers, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Support Ticket", engine: "Ticketing Module", desc: "Categorized priority issue logged", icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
        { label: "SLA Sentinel", engine: "AMC Watchdog", desc: "15-minute response timer assigned", icon: Clock, color: "text-purple-600", bg: "bg-purple-50" },
        { label: "Resolution", engine: "Engineering Sync", desc: "Signed-off deliverable closed", icon: Check, color: "text-emerald-600", bg: "bg-emerald-50" },
      ]
    },
    {
      id: "developer",
      name: "Launchpad Talent Flow",
      tagline: "From Algorithmic Challenge to Career DNA",
      trigger: "Developer Starts AI Coding Challenge",
      stages: [
        { label: "AI Challenge", engine: "Launchpad AI", desc: "Unique algorithmic problem synthesized", icon: Brain, color: "text-purple-600", bg: "bg-purple-50" },
        { label: "Code Commit", engine: "GitHub Pull", desc: "Student pushes branch repository", icon: GitCommit, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Docker Sandbox", engine: "Runner Sandbox", desc: "Ephemeral isolated test execution", icon: Terminal, color: "text-emerald-600", bg: "bg-emerald-50" },
        { label: "Verification", engine: "AST Code Engine", desc: "Big-O runtime & syntax audits", icon: ShieldCheck, color: "text-amber-600", bg: "bg-amber-50" },
        { label: "Career DNA", engine: "Skill Passport", desc: "Immutable skill score verified", icon: Award, color: "text-cyan-600", bg: "bg-cyan-50" },
      ]
    },
    {
      id: "sre",
      name: "Guardian SRE & Defense Flow",
      tagline: "Sub-2-Second Threat Detection & Auto-Heal",
      trigger: "Heuristic Anomaly Detected on Production Node",
      stages: [
        { label: "Telemetry", engine: "Guardian Daemon", desc: "Kernel eBPF & /proc monitoring", icon: Cloud, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Threat Detect", engine: "Heuristic Sentinel", desc: "Malicious file write identified", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
        { label: "2s Containment", engine: "Active Defense", desc: "Chattr +i & process SIGSTOP", icon: Lock, color: "text-purple-600", bg: "bg-purple-50" },
        { label: "Auto Remediation", engine: "SRE Runbook", desc: "Microservice rebooted & healed", icon: RefreshCw, color: "text-emerald-600", bg: "bg-emerald-50" },
        { label: "Audit Log", engine: "Notification Hub", desc: "Telegram & VAPID push dispatched", icon: Bell, color: "text-cyan-600", bg: "bg-cyan-50" },
      ]
    },
    {
      id: "workforce",
      name: "HRMS & BioAuth Flow",
      tagline: "Biometric Identity to Automated Monthly Payroll",
      trigger: "Employee Opens Browser on Working Day",
      stages: [
        { label: "Webcam Scan", engine: "BioAuth AI", desc: "Browser camera captures facial frame", icon: ScanFace, color: "text-emerald-600", bg: "bg-emerald-50" },
        { label: "Vector Match", engine: "DPDP Database", desc: "128-D vector verified in local PG", icon: Database, color: "text-blue-600", bg: "bg-blue-50" },
        { label: "Sprint Tasks", engine: "Task Tracker", desc: "Daily tickets & time logs active", icon: Briefcase, color: "text-purple-600", bg: "bg-purple-50" },
        { label: "MeetPass Gate", engine: "MeetPass Gateway", desc: "PIN-secured daily standup call", icon: Video, color: "text-cyan-600", bg: "bg-cyan-50" },
        { label: "Payroll Calc", engine: "Workspace HRMS", desc: "Zero-touch salary slip generated", icon: CreditCard, color: "text-amber-600", bg: "bg-amber-50" },
      ]
    }
  ];



  // ──────────────────────────────────────────────────────────────────────────
  // 08 — SAAS STACK DISRUPTION DATA (No 3rd party brand logos)
  // ──────────────────────────────────────────────────────────────────────────
  const saasDisruptions = [
    {
      category: "CRM & Sales Pipeline",
      fragmented: "Expensive user seats, data locked in foreign cloud servers",
      costRange: "₹25,000 – ₹55,000 / mo",
      norbitEngine: "nORBIT Enterprise CRM & Leads",
      inHouseBenefit: "Unlimited seats, direct PostgreSQL sync, versioned proposals"
    },
    {
      category: "Project & Task Management",
      fragmented: "Per-seat fees for separate Kanban boards, sprint tools, and disconnected issue trackers",
      costRange: "₹25,000 – ₹50,000 / mo",
      norbitEngine: "nORBIT Agile Projects & Sprints",
      inHouseBenefit: "Unlimited seats, native Git sprint tracking, milestone roadmaps, and client sync"
    },
    {
      category: "Billing, Invoicing & Payments",
      fragmented: "Separate subscription billing platforms taking 2-3% platform cuts",
      costRange: "₹20,000 – ₹45,000 / mo",
      norbitEngine: "nORBIT Billing & Payment Gateway",
      inHouseBenefit: "Direct gateway pass-through, automated GST e-invoices, zero cut"
    },
    {
      category: "Infrastructure Monitoring & SRE",
      fragmented: "High host fees, per-metric billing, separate alerting tools",
      costRange: "₹35,000 – ₹70,000 / mo",
      norbitEngine: "NeuraVolt Guardian SRE",
      inHouseBenefit: "Kernel /proc telemetry, sub-5s self-healing, zero agent fees"
    },
    {
      category: "Cybersecurity & EDR Defense",
      fragmented: "Complex endpoint tools, heavyweight agents slowing down nodes",
      costRange: "₹30,000 – ₹60,000 / mo",
      norbitEngine: "nORBIT Active Defense",
      inHouseBenefit: "2-sec webshell quarantine, Bcrypt 2-step command governance"
    },
    {
      category: "HRMS, Attendance & Identity",
      fragmented: "Per-employee monthly charges, external biometric cloud exposure",
      costRange: "₹20,000 – ₹40,000 / mo",
      norbitEngine: "nORBIT HRMS & BioAuth",
      inHouseBenefit: "128-D facial vectors stay inside local DB; DPDP-2023 compliant"
    },
    {
      category: "Talent Assessment & Code Tests",
      fragmented: "Costly per-assessment test credits and static plagiarized questions",
      costRange: "₹35,000 – ₹60,000 / mo",
      norbitEngine: "NeuraVolt Launchpad",
      inHouseBenefit: "AI-generated dynamic challenges, sandboxed Docker execution"
    },
    {
      category: "Notification Infrastructure",
      fragmented: "High SMS/WhatsApp vendor markup rates per outgoing notification",
      costRange: "₹20,000 – ₹30,000 / mo",
      norbitEngine: "nORBIT Notification Hub",
      inHouseBenefit: "In-house Telegram bots, VAPID Web Push, direct WhatsApp gateway"
    }
  ];

  return (
    <Layout>
      <Seo
        title="nORBIT: Sovereign All-in-One Enterprise Operating System | Neuravolt"
        description="One Platform. Your Entire Business. nORBIT brings together Guardian SRE, Business & Revenue ERP, Multi-Tenant Client Portal, Launchpad AI Talent Engine, and HRMS BioAuth."
        keywords="nORBIT, Sovereign Enterprise OS, Neuravolt Guardian, Launchpad, BioAuth, Payment Gateway, Client Portal, HRMS, SRE Cyber Defense, DPDP 2023"
        path="/norbit"
        jsonLd={jsonLd}
      />

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 01 — HERO: WHAT IS nORBIT? (EXACT REFERENCE DESIGN)                    */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="overview" className="scroll-mt-24 relative pt-32 pb-16 sm:pb-20 px-5 sm:px-8 bg-gradient-to-b from-[#FAF9F6] via-white to-[#FAF9F6] text-[#0B0B0B] overflow-hidden border-b border-black/[0.06]">
        {/* Soft background ambient glow */}
        <div className="absolute top-1/4 right-5 w-[650px] h-[650px] bg-gradient-to-br from-[#FF5A1F]/10 via-blue-500/5 to-purple-500/5 blur-[140px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-5 text-left">
              {/* Eyebrow Pill */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-black/10 shadow-2xs mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
                <span className="text-[10px] sm:text-[11px] font-mono font-semibold tracking-wider text-neutral-700 uppercase">
                  SOVEREIGN ALL-IN-ONE ENTERPRISE OPERATING SYSTEM
                </span>
              </div>

              {/* Logo Emblem */}
              <div className="mb-5 flex items-center gap-3">
                <img
                  src="/brand/norbit-logo.png"
                  alt="nORBIT: One Platform. Your Entire Business."
                  className="h-10 sm:h-12 w-auto object-contain select-none"
                />
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-5xl lg:text-[50px] font-display font-bold tracking-tight text-[#0B0B0B] leading-[1.12] mb-5">
                Your Entire Business.{" "}
                <span className="text-[#FF5A1F] block mt-1">One Autonomous Operating System.</span>
              </h1>

              {/* Subheading */}
              <p className="text-neutral-600 text-sm sm:text-base leading-relaxed mb-8 max-w-lg">
                nORBIT is Neuravolt's sovereign enterprise operating system uniting in-house <strong>CRM</strong>, <strong>Project Management</strong>, Billing ERP, Client Portals, AI Talent Verification, and Autonomous SRE into one platform.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-3.5 mb-6">
                <button
                  onClick={() => openConsultationModal("Schedule Architecture Demo")}
                  className="px-6 py-3.5 rounded-full bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-semibold text-sm transition-all duration-300 shadow-lg shadow-[#FF5A1F]/25 flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Schedule Architecture Demo</span>
                  <ArrowRight className="w-4 h-4 ml-0.5" />
                </button>
                <a
                  href="#five-pillars"
                  className="px-6 py-3.5 rounded-full bg-white hover:bg-neutral-100 text-neutral-800 font-semibold text-sm transition-all duration-300 border border-black/10 shadow-2xs flex items-center gap-2"
                >
                  <span>Explore the 5 Pillars</span>
                  <ArrowRight className="w-4 h-4 text-neutral-500" />
                </a>
              </div>
            </div>

            {/* Right Visual: Isometric 3D Platform with 5 Connected Floating Cards */}
            <div className="lg:col-span-7">
              <div className="relative w-full max-w-[660px] h-[520px] sm:h-[540px] mx-auto select-none">
                {/* Ambient Center Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-tr from-blue-400/20 via-[#FF5A1F]/25 to-purple-400/20 blur-3xl rounded-full pointer-events-none" />

                {/* SVG Connecting Curved Flow Lines & Flow Badges */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                  <defs>
                    <linearGradient id="flowGradBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#60A5FA" stopOpacity="0.2" />
                    </linearGradient>
                    <linearGradient id="flowGradPurple" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#A855F7" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#C084FC" stopOpacity="0.2" />
                    </linearGradient>
                  </defs>

                  {/* Line from Guardian (top center) to Center Platform */}
                  <path d="M 370 120 C 370 180, 360 220, 340 250" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeDasharray="4 4" className="opacity-60" />

                  {/* Line from Launchpad (top right) to Center Platform */}
                  <path d="M 520 180 C 480 210, 420 240, 370 260" fill="none" stroke="#A855F7" strokeWidth="1.5" strokeDasharray="4 4" className="opacity-60" />

                  {/* Line from Client Portal (mid left) to Center Platform */}
                  <path d="M 280 340 C 300 320, 310 290, 330 280" fill="none" stroke="#06B6D4" strokeWidth="1.5" strokeDasharray="4 4" className="opacity-60" />

                  {/* Line from Center Platform to HRMS (bottom right) */}
                  <path d="M 360 310 C 400 340, 460 370, 520 380" fill="none" stroke="#10B981" strokeWidth="1.5" strokeDasharray="4 4" className="opacity-60" />
                </svg>

                {/* Flow Pill Badges */}
                <div className="absolute top-[175px] left-[380px] z-20 px-2 py-0.5 rounded-md bg-white border border-blue-200/80 shadow-2xs text-[9px] font-mono text-blue-600 font-semibold">
                  Data Flow
                </div>
                <div className="absolute top-[225px] right-[100px] z-20 px-2 py-0.5 rounded-md bg-white border border-purple-200/80 shadow-2xs text-[9px] font-mono text-purple-600 font-semibold">
                  Event Mesh
                </div>
                <div className="absolute bottom-[200px] left-[230px] z-20 px-2 py-0.5 rounded-md bg-white border border-cyan-200/80 shadow-2xs text-[9px] font-mono text-cyan-700 font-semibold">
                  Unified Database
                </div>
                <div className="absolute bottom-[20px] left-[320px] z-20 px-2 py-0.5 rounded-md bg-white border border-emerald-200/80 shadow-2xs text-[9px] font-mono text-emerald-700 font-semibold">
                  Automated Workflows
                </div>

                {/* ──────────────────────────────────────────────────────────── */}
                {/* CENTER: ISOMETRIC 3D LAYERED GLASS PLATFORM                 */}
                {/* ──────────────────────────────────────────────────────────── */}
                <div className="absolute top-[200px] sm:top-[210px] left-[240px] sm:left-[270px] z-10 [perspective:1200px] group cursor-pointer">
                  {/* Layer 3 - Base shadow plate */}
                  <div className="w-52 h-44 rounded-[32px] bg-gradient-to-tr from-blue-200/50 via-indigo-100/50 to-orange-100/40 shadow-[0_30px_70px_-15px_rgba(59,130,246,0.35)] [transform:rotateX(60deg)_rotateZ(-45deg)] transition-transform duration-500 group-hover:scale-105" />

                  {/* Layer 2 - Intermediate glass rim */}
                  <div className="absolute top-2 left-2 w-48 h-40 rounded-[28px] bg-gradient-to-b from-white/95 to-blue-50/80 border border-white shadow-md [transform:rotateX(60deg)_rotateZ(-45deg)] transition-transform duration-500 group-hover:-translate-y-2" />

                  {/* Layer 1 - Top Isometric Platform */}
                  <div className="absolute -top-3 left-0 w-52 h-44 rounded-[28px] bg-white border border-blue-200/60 shadow-[0_15px_35px_-5px_rgba(0,0,0,0.08)] [transform:rotateX(60deg)_rotateZ(-45deg)] flex flex-col items-center justify-center p-4 text-center transition-transform duration-500 group-hover:-translate-y-4">
                    <img
                      src="/brand/norbit-logo.png"
                      alt="nORBIT"
                      className="w-24 sm:w-28 h-auto object-contain select-none mb-1 drop-shadow-xs"
                    />
                    <div className="text-[10px] font-mono font-semibold text-neutral-800 leading-tight">
                      Sovereign Enterprise OS
                    </div>
                    <div className="text-[9px] font-mono font-bold text-[#FF5A1F] mt-0.5">
                      5 Sovereign Pillars
                    </div>
                  </div>
                </div>

                {/* ──────────────────────────────────────────────────────────── */}
                {/* 5 FLOATING PILLAR CARDS                                     */}
                {/* ──────────────────────────────────────────────────────────── */}

                {/* Card 1: NeuraVolt Guardian (Top Center-Right) */}
                <div className="absolute top-0 right-28 sm:right-36 z-20 w-52 sm:w-56 p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border border-black/10 shadow-lg hover:shadow-xl hover:border-blue-400 transition-all duration-300 text-left">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 grid place-items-center shrink-0">
                        <ShieldCheck className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-neutral-900 leading-tight">NeuraVolt Guardian</div>
                        <div className="text-[9px] text-neutral-500 font-medium">SRE & Cyber Defense</div>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full bg-blue-50 text-blue-600 grid place-items-center text-[10px] font-bold">
                      →
                    </div>
                  </div>
                  <div className="space-y-0.5 text-[10px] text-neutral-600 pl-1 border-t border-black/5 pt-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-blue-500" />
                      <span>Server Monitoring</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-blue-500" />
                      <span>Threat Containment</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-blue-500" />
                      <span>Auto Remediation</span>
                    </div>
                  </div>
                </div>

                {/* Card 2: CRM & Revenue ERP (Top Left - Highlighting CRM) */}
                <div className="absolute top-12 left-2 sm:left-4 z-20 w-52 sm:w-56 p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border border-black/10 shadow-lg hover:shadow-xl hover:border-amber-400 transition-all duration-300 text-left">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 grid place-items-center shrink-0">
                        <BarChart3 className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-neutral-900 leading-tight">CRM & Revenue ERP</div>
                        <div className="text-[9px] text-[#FF5A1F] font-semibold">In-House CRM • Leads • Billing</div>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full bg-amber-50 text-amber-600 grid place-items-center text-[10px] font-bold">
                      →
                    </div>
                  </div>
                  <div className="space-y-0.5 text-[10px] text-neutral-600 pl-1 border-t border-black/5 pt-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-amber-500" />
                      <span className="font-semibold text-neutral-800">Enterprise CRM & Leads</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-amber-500" />
                      <span>GST Invoices & Returns</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-amber-500" />
                      <span>Payment Gateway & Quotes</span>
                    </div>
                  </div>
                </div>

                {/* Card 3: NeuraVolt Launchpad (Top Right) */}
                <div className="absolute top-10 right-0 sm:right-2 z-20 w-52 sm:w-56 p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border border-black/10 shadow-lg hover:shadow-xl hover:border-purple-400 transition-all duration-300 text-left">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 grid place-items-center shrink-0">
                        <Rocket className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-neutral-900 leading-tight">NeuraVolt Launchpad</div>
                        <div className="text-[9px] text-neutral-500 font-medium">AI Talent & Verification</div>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full bg-purple-50 text-purple-600 grid place-items-center text-[10px] font-bold">
                      →
                    </div>
                  </div>
                  <div className="space-y-0.5 text-[10px] text-neutral-600 pl-1 border-t border-black/5 pt-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-purple-500" />
                      <span>AI Challenges</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-purple-500" />
                      <span>Docker Sandboxes</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-purple-500" />
                      <span>Career DNA & Scores</span>
                    </div>
                  </div>
                </div>

                {/* Card 4: Client Portal (Mid-Left) */}
                <div className="absolute bottom-20 left-4 sm:left-10 z-20 w-52 sm:w-56 p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border border-black/10 shadow-lg hover:shadow-xl hover:border-cyan-400 transition-all duration-300 text-left">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 grid place-items-center shrink-0">
                        <Users className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-neutral-900 leading-tight">Client Portal</div>
                        <div className="text-[9px] text-neutral-500 font-medium">Tenant Self-Service</div>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full bg-cyan-50 text-cyan-600 grid place-items-center text-[10px] font-bold">
                      →
                    </div>
                  </div>
                  <div className="space-y-0.5 text-[10px] text-neutral-600 pl-1 border-t border-black/5 pt-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-cyan-500" />
                      <span>Client Invoices</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-cyan-500" />
                      <span>Support Tickets</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-cyan-500" />
                      <span>Project Health & SLA</span>
                    </div>
                  </div>
                </div>

                {/* Card 5: Project Management & HRMS (Bottom Right - Highlighting Project Management) */}
                <div className="absolute bottom-6 right-4 sm:right-10 z-20 w-56 sm:w-60 p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border border-black/10 shadow-lg hover:shadow-xl hover:border-emerald-400 transition-all duration-300 text-left">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
                        <ScanFace className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-neutral-900 leading-tight">Project Management & HRMS</div>
                        <div className="text-[9px] text-emerald-700 font-semibold">Projects • Sprints • BioAuth</div>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center text-[10px] font-bold">
                      →
                    </div>
                  </div>
                  <div className="space-y-0.5 text-[10px] text-neutral-600 pl-1 border-t border-black/5 pt-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-emerald-500" />
                      <span className="font-semibold text-neutral-800">Project & Sprint Tracking</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-emerald-500" />
                      <span>Task Kanban & Milestones</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-emerald-500" />
                      <span>AI Face Attendance & MeetPass</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ────────────────────────────────────────────────────────────────── */}
          {/* HORIZONTAL PROOF METRICS BAR (EXACT REFERENCE DESIGN)             */}
          {/* ────────────────────────────────────────────────────────────────── */}
          <div className="mt-12 sm:mt-16 p-4 sm:p-5 rounded-3xl bg-white border border-black/10 shadow-sm grid grid-cols-2 md:grid-cols-5 gap-4 items-center">
            {/* Metric 1: 5 Pillars */}
            <div className="flex items-center gap-3 p-2 text-left">
              <div className="w-11 h-11 rounded-2xl bg-orange-50 text-[#FF5A1F] grid place-items-center shrink-0">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm sm:text-base font-bold text-neutral-900 leading-tight">5 Pillars</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Unified Enterprise Architecture</div>
              </div>
            </div>

            {/* Metric 2: ₹0 Extra */}
            <div className="flex items-center gap-3 p-2 text-left">
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center shrink-0 font-bold text-lg">
                ₹
              </div>
              <div>
                <div className="text-sm sm:text-base font-bold text-emerald-600 leading-tight">₹0 Extra</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">SaaS Sprawl In-House</div>
              </div>
            </div>

            {/* Metric 3: 100% Data Sovereignty */}
            <div className="flex items-center gap-3 p-2 text-left">
              <div className="w-11 h-11 rounded-2xl bg-purple-50 text-purple-600 grid place-items-center shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm sm:text-base font-bold text-neutral-900 leading-tight">100%</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Data Sovereignty (DPDP)</div>
              </div>
            </div>

            {/* Metric 4: Sub-2s Active Shield */}
            <div className="flex items-center gap-3 p-2 text-left">
              <div className="w-11 h-11 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center shrink-0">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm sm:text-base font-bold text-neutral-900 leading-tight">Sub-2s</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Guardian Active Shield</div>
              </div>
            </div>

            {/* Metric 5: Complete Business OS */}
            <div className="flex items-center gap-3 p-2 text-left col-span-2 md:col-span-1 border-t md:border-t-0 md:border-l border-black/5 pt-3 md:pt-0 md:pl-4">
              <div className="w-11 h-11 rounded-2xl bg-amber-50 text-amber-600 grid place-items-center shrink-0">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-bold text-neutral-900 leading-tight">Complete Business OS</div>
                <div className="text-[10px] text-neutral-500 mt-0.5 leading-snug">
                  From CRM & Projects to Billing, SRE & BioAuth.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 02 — THE CORE nORBIT PHILOSOPHY & SAAS DISRUPTION (EXACT REFERENCE)   */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="philosophy" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-white border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* Left Content */}
            <div className="lg:col-span-4 text-left">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50/80 border border-orange-200/60 text-[10px] sm:text-[11px] font-mono font-semibold text-neutral-700 uppercase mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
                <span>THE CORE nORBIT PHILOSOPHY</span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-display font-bold text-[#0B0B0B] leading-tight mb-4 tracking-tight">
                Embed In-House. <br />
                <span className="text-[#FF5A1F]">Eliminate Dependency.</span>
              </h2>

              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed mb-4">
                Modern tech businesses waste lakhs every month subscribing to 8+ disconnected SaaS platforms, scattering customer data, paying recurring seat taxes, and running manual cross-tool glue scripts.
              </p>

              <p className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                nORBIT replaces this fragmentation by bringing infrastructure, business revenue, client self-service, developer evaluation, and workforce identity into one sovereign in-house stack.
              </p>
            </div>

            {/* Right Comparison: The Old Way vs With nORBIT */}
            <div className="lg:col-span-8 flex flex-col md:flex-row items-center gap-4">
              {/* Left Card: THE OLD WAY */}
              <div className="w-full md:w-1/2 p-5 sm:p-6 rounded-3xl bg-[#FAF9F6] border border-red-200/60 text-left shadow-2xs">
                {/* Header */}
                <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold text-red-600 uppercase mb-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  <span>THE OLD WAY</span>
                </div>
                <h3 className="text-base font-bold text-neutral-900 mb-4">8+ Disconnected SaaS Tools</h3>

                {/* 3x3 Grid of Generic Systems */}
                <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <Users className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">CRM & Sales</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <FileText className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">Billing & Invoices</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <Server className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">Infra Monitoring</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <Shield className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">Cybersecurity</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <Users className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">HRMS</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <GraduationCap className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">Talent Assessment</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <Headphones className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">Client Support</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center">
                    <Layers className="w-3.5 h-3.5 text-neutral-600 mb-1" />
                    <span className="text-[9px] font-semibold text-neutral-800 truncate">Project Tracking</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-black/5 shadow-2xs flex flex-col items-center justify-center text-[10px] font-mono text-neutral-400">
                    ... and more
                  </div>
                </div>

                {/* Red Cross Points */}
                <div className="space-y-1.5 text-xs text-neutral-600 pt-3 border-t border-red-100">
                  <div className="flex items-center gap-2">
                    <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    <span className="text-[11px]">Multiple logins & expensive subscriptions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    <span className="text-[11px]">Scattered data across different platforms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                    <span className="text-[11px]">No true data ownership</span>
                  </div>
                </div>
              </div>

              {/* Middle Transition Conduit */}
              <div className="shrink-0 w-8 h-8 rounded-full bg-white border border-black/10 shadow-2xs grid place-items-center text-neutral-400 my-2 md:my-0">
                <ChevronRight className="w-4 h-4 text-[#FF5A1F]" />
              </div>

              {/* Right Card: WITH nORBIT */}
              <div className="w-full md:w-1/2 p-5 sm:p-6 rounded-3xl bg-gradient-to-b from-emerald-50/40 via-white to-emerald-50/20 border border-emerald-300 text-left shadow-2xs">
                {/* Header */}
                <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold text-emerald-600 uppercase mb-1">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  <span>WITH nORBIT</span>
                </div>
                <h3 className="text-base font-bold text-neutral-900 mb-4">One Sovereign Platform</h3>

                {/* Isometric Mini Platform */}
                <div className="py-4 text-center">
                  <div className="inline-block p-4 rounded-2xl bg-white border border-blue-200/80 shadow-md">
                    <img
                      src="/brand/norbit-logo.png"
                      alt="nORBIT"
                      className="w-24 sm:w-28 h-auto object-contain select-none mx-auto mb-1"
                    />
                    <div className="text-[10px] font-mono text-neutral-500 font-semibold">
                      Sovereign Enterprise OS
                    </div>
                  </div>
                </div>

                {/* 4 Green Checkmark Pills */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-emerald-100">
                  <div className="p-2 rounded-xl bg-white border border-emerald-200/80 flex items-center gap-1.5 text-[10px] font-semibold text-neutral-800 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Unified Database</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-emerald-200/80 flex items-center gap-1.5 text-[10px] font-semibold text-neutral-800 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Unified Identity</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-emerald-200/80 flex items-center gap-1.5 text-[10px] font-semibold text-neutral-800 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Unified Workflows</span>
                  </div>
                  <div className="p-2 rounded-xl bg-white border border-emerald-200/80 flex items-center gap-1.5 text-[10px] font-semibold text-neutral-800 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>Zero External Dependencies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 03 — THE 5 CORE ENGINES OF nORBIT (EXACT REFERENCE DESIGN)             */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="five-pillars" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-[#FAF9F6] border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto text-center">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-black/10 text-[11px] font-mono font-semibold text-neutral-700 uppercase mb-4 shadow-2xs">
            <Layers className="w-3.5 h-3.5 text-[#FF5A1F]" />
            THE 5 TRUE PILLARS
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#0B0B0B] mb-4 tracking-tight">
            The 5 Core Engines of nORBIT
          </h2>
          <p className="text-neutral-600 text-sm sm:text-base max-w-2xl mx-auto mb-10 leading-relaxed">
            Five natively integrated engines powering infrastructure, capital, clients, talent, and workforce on one sovereign platform.
          </p>

          {/* Continuous Top Connecting Line with Color-Coded Pulsing Nodes - EXACTLY CENTERED OVER EACH CARD */}
          <div className="relative mb-6 hidden lg:block">
            <div className="grid grid-cols-5 gap-4.5 relative items-center">
              {/* Continuous horizontal connecting line passing through center of all nodes */}
              <div className="absolute left-[10%] right-[10%] top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-blue-400 via-[#FF5A1F] via-cyan-400 via-purple-400 to-emerald-400 rounded-full opacity-60 z-0 pointer-events-none" />

              {/* Node 1 - Centered in Col 1 */}
              <div className="flex justify-center z-10">
                <div className="w-3.5 h-3.5 rounded-full bg-blue-500 shadow-md shadow-blue-500/50 ring-4 ring-blue-100" />
              </div>

              {/* Node 2 - Centered in Col 2 */}
              <div className="flex justify-center z-10">
                <div className="w-3.5 h-3.5 rounded-full bg-[#FF5A1F] shadow-md shadow-[#FF5A1F]/50 ring-4 ring-orange-100" />
              </div>

              {/* Node 3 - Centered in Col 3 */}
              <div className="flex justify-center z-10">
                <div className="w-3.5 h-3.5 rounded-full bg-cyan-500 shadow-md shadow-cyan-500/50 ring-4 ring-cyan-100" />
              </div>

              {/* Node 4 - Centered in Col 4 */}
              <div className="flex justify-center z-10">
                <div className="w-3.5 h-3.5 rounded-full bg-purple-500 shadow-md shadow-purple-500/50 ring-4 ring-purple-100" />
              </div>

              {/* Node 5 - Centered in Col 5 */}
              <div className="flex justify-center z-10">
                <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50 ring-4 ring-emerald-100" />
              </div>
            </div>
          </div>

          {/* 5 Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4.5">
            {/* Card 1: NeuraVolt Guardian */}
            <div className="p-5 rounded-3xl bg-white border border-black/10 hover:border-blue-400/80 hover:shadow-xl transition-all duration-300 flex flex-col justify-between text-left group">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-mono text-neutral-400 font-semibold">Pillar 01</span>
                  <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200/60">
                    The Shield of nORBIT
                  </span>
                </div>

                {/* 3D Visual Art: Server Rack with Glowing Blue Shield */}
                <div className="h-28 my-3 rounded-2xl bg-gradient-to-b from-blue-50/80 via-white to-blue-50/40 flex items-center justify-center relative overflow-hidden border border-blue-100/50">
                  <div className="w-20 h-20 rounded-full bg-blue-400/20 blur-md absolute pointer-events-none" />
                  <div className="relative flex flex-col items-center">
                    {/* 3D Stacked Server Blades */}
                    <div className="w-16 h-3 rounded-md bg-blue-100 border border-blue-200 shadow-xs mb-1 flex items-center justify-between px-1.5">
                      <div className="w-1 h-1 rounded-full bg-blue-500 animate-pulse" />
                      <div className="w-8 h-1 rounded-full bg-blue-200" />
                    </div>
                    <div className="w-18 h-3 rounded-md bg-blue-200/80 border border-blue-300/80 shadow-xs mb-1 flex items-center justify-between px-1.5">
                      <div className="w-1 h-1 rounded-full bg-blue-600 animate-pulse" />
                      <div className="w-10 h-1 rounded-full bg-blue-300" />
                    </div>
                    <div className="w-20 h-4 rounded-md bg-gradient-to-r from-blue-500 to-indigo-600 shadow-md flex items-center justify-between px-2 text-white">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <ShieldCheck className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                </div>

                <h3 className="text-base font-bold text-neutral-900 group-hover:text-blue-600 transition-colors">
                  NeuraVolt Guardian
                </h3>
                <div className="text-[10px] font-mono text-neutral-500 font-semibold mt-0.5 mb-2">
                  Autonomous SRE & Cyber Defense
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                  2-second webshell containment and autonomous infrastructure self-healing.
                </p>

                {/* 4 Bullet Points */}
                <div className="space-y-2 text-xs text-neutral-700 pl-0.5 border-t border-black/5 pt-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Real-time Server Monitoring</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Active Threat Containment</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Auto Remediation</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Fleet Operations</span>
                  </div>
                </div>
              </div>

              <Link
                to="/guardian"
                className="w-full mt-5 py-2.5 rounded-full bg-white hover:bg-neutral-50 text-neutral-800 text-xs font-semibold border border-black/10 shadow-2xs flex items-center justify-center gap-1.5 transition-all group-hover:border-blue-400"
              >
                <span>Inspect Engine</span>
                <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            {/* Card 2: Business & Revenue (Highlighted Card with Flame Badge) */}
            <div className="p-5 rounded-3xl bg-white border-2 border-[#FF5A1F] shadow-lg shadow-[#FF5A1F]/10 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between text-left group relative">
              {/* Extra Top Highlight Badge */}
              <div className="flex items-center justify-center mb-1">
                <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-orange-100 text-[#FF5A1F] border border-orange-200">
                  🔥 Most Impactful
                </span>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-mono text-neutral-400 font-semibold">Pillar 02</span>
                  <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200/60">
                    CRM & Financial Engine
                  </span>
                </div>

                {/* 3D Visual Art: 3D Bar Chart & Rupee Coin */}
                <div className="h-28 my-3 rounded-2xl bg-gradient-to-b from-orange-50/80 via-white to-orange-50/40 flex items-center justify-center relative overflow-hidden border border-orange-100/50">
                  <div className="w-20 h-20 rounded-full bg-orange-400/20 blur-md absolute pointer-events-none" />
                  <div className="relative flex items-end justify-center gap-1.5 h-14">
                    {/* Stepped Bars */}
                    <div className="w-3.5 h-6 rounded-t-md bg-orange-200 border border-orange-300/80" />
                    <div className="w-3.5 h-10 rounded-t-md bg-orange-300 border border-orange-400/80" />
                    <div className="w-3.5 h-14 rounded-t-md bg-gradient-to-t from-orange-500 to-[#FF5A1F] shadow-md flex items-center justify-center" />
                    {/* 3D Rupee Coin */}
                    <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-amber-400 to-yellow-300 border border-amber-500 shadow-md flex items-center justify-center text-neutral-900 font-bold text-xs ml-1 -translate-y-2 animate-bounce">
                      ₹
                    </div>
                  </div>
                </div>

                <h3 className="text-base font-bold text-neutral-900 group-hover:text-[#FF5A1F] transition-colors">
                  CRM & Revenue Engine
                </h3>
                <div className="text-[10px] font-mono text-neutral-500 font-semibold mt-0.5 mb-2">
                  Enterprise CRM + Lead Pipeline + ERP + Billing
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                  End-to-end client lifecycle from CRM lead capture and quotation pipelines to automated GST invoice, tax returns, and bank deposit.
                </p>

                {/* 4 Bullet Points */}
                <div className="space-y-2 text-xs text-neutral-700 pl-0.5 border-t border-black/5 pt-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Enterprise CRM & Lead Scoring</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">GST Invoicing & Tax Returns</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Payment Gateway Integration</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Quotations & Asset Renewals</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => openConsultationModal("Inspect Business & Revenue Engine")}
                className="w-full mt-5 py-2.5 rounded-full bg-[#FF5A1F] hover:bg-[#ff6d38] text-white text-xs font-semibold shadow-md shadow-[#FF5A1F]/25 flex items-center justify-center gap-1.5 transition-all"
              >
                <span>Inspect Engine</span>
                <ArrowRight className="w-3.5 h-3.5 text-white group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            {/* Card 3: Client Portal */}
            <div className="p-5 rounded-3xl bg-white border border-black/10 hover:border-cyan-400/80 hover:shadow-xl transition-all duration-300 flex flex-col justify-between text-left group">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-mono text-neutral-400 font-semibold">Pillar 03</span>
                  <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200/60">
                    Self-Service Portal
                  </span>
                </div>

                {/* 3D Visual Art: 3D Floating Portal Card */}
                <div className="h-28 my-3 rounded-2xl bg-gradient-to-b from-cyan-50/80 via-white to-cyan-50/40 flex items-center justify-center relative overflow-hidden border border-cyan-100/50">
                  <div className="w-20 h-20 rounded-full bg-cyan-400/20 blur-md absolute pointer-events-none" />
                  <div className="relative [perspective:600px]">
                    <div className="w-20 h-14 rounded-xl bg-white border border-cyan-200 shadow-md p-1.5 flex flex-col justify-between [transform:rotateX(20deg)_rotateY(-15deg)]">
                      <div className="flex items-center justify-between">
                        <div className="w-4 h-4 rounded-full bg-cyan-100 grid place-items-center text-[8px] text-cyan-700 font-bold">👤</div>
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      </div>
                      <div className="space-y-1">
                        <div className="w-full h-1.5 rounded-full bg-cyan-100" />
                        <div className="w-3/4 h-1.5 rounded-full bg-cyan-50" />
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="text-base font-bold text-neutral-900 group-hover:text-cyan-600 transition-colors">
                  Client Portal
                </h3>
                <div className="text-[10px] font-mono text-neutral-500 font-semibold mt-0.5 mb-2">
                  Multi-Tenant White-Label
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                  Dedicated self-service interface for every client with zero internal system exposure.
                </p>

                {/* 4 Bullet Points */}
                <div className="space-y-2 text-xs text-neutral-700 pl-0.5 border-t border-black/5 pt-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Client Invoices</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Support Tickets</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Project Health & SLA</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">White-Label Portals</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => openConsultationModal("Inspect Client Portal Engine")}
                className="w-full mt-5 py-2.5 rounded-full bg-white hover:bg-neutral-50 text-neutral-800 text-xs font-semibold border border-black/10 shadow-2xs flex items-center justify-center gap-1.5 transition-all group-hover:border-cyan-400"
              >
                <span>Inspect Engine</span>
                <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            {/* Card 4: NeuraVolt Launchpad */}
            <div className="p-5 rounded-3xl bg-white border border-black/10 hover:border-purple-400/80 hover:shadow-xl transition-all duration-300 flex flex-col justify-between text-left group">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-mono text-neutral-400 font-semibold">Pillar 04</span>
                  <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200/60">
                    The Talent Engine
                  </span>
                </div>

                {/* 3D Visual Art: AI Brain Chip */}
                <div className="h-28 my-3 rounded-2xl bg-gradient-to-b from-purple-50/80 via-white to-purple-50/40 flex items-center justify-center relative overflow-hidden border border-purple-100/50">
                  <div className="w-20 h-20 rounded-full bg-purple-400/20 blur-md absolute pointer-events-none" />
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-500 shadow-md flex items-center justify-center text-white p-2">
                      <Brain className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -bottom-1.5 -right-1.5 px-1.5 py-0.5 rounded bg-white text-[8px] font-mono text-purple-600 font-bold shadow-xs border border-purple-200">
                      AST/AI
                    </div>
                  </div>
                </div>

                <h3 className="text-base font-bold text-neutral-900 group-hover:text-purple-600 transition-colors">
                  NeuraVolt Launchpad
                </h3>
                <div className="text-[10px] font-mono text-neutral-500 font-semibold mt-0.5 mb-2">
                  AI Talent & Verification
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                  Autonomous candidate evaluation via algorithmic AI challenges and Docker sandboxes.
                </p>

                {/* 4 Bullet Points */}
                <div className="space-y-2 text-xs text-neutral-700 pl-0.5 border-t border-black/5 pt-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">AI Challenge Generation</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Docker Sandbox Execution</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Automated Code Verification</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Career DNA & Skill Score</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => openConsultationModal("Inspect Launchpad Engine")}
                className="w-full mt-5 py-2.5 rounded-full bg-white hover:bg-neutral-50 text-neutral-800 text-xs font-semibold border border-black/10 shadow-2xs flex items-center justify-center gap-1.5 transition-all group-hover:border-purple-400"
              >
                <span>Inspect Engine</span>
                <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>

            {/* Card 5: HRMS + BioAuth + Meetings */}
            <div className="p-5 rounded-3xl bg-white border border-black/10 hover:border-emerald-400/80 hover:shadow-xl transition-all duration-300 flex flex-col justify-between text-left group">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-mono text-neutral-400 font-semibold">Pillar 05</span>
                  <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                    Projects & Workforce
                  </span>
                </div>

                {/* 3D Visual Art: Biometric ID Badge */}
                <div className="h-28 my-3 rounded-2xl bg-gradient-to-b from-emerald-50/80 via-white to-emerald-50/40 flex items-center justify-center relative overflow-hidden border border-emerald-100/50">
                  <div className="w-20 h-20 rounded-full bg-emerald-400/20 blur-md absolute pointer-events-none" />
                  <div className="relative">
                    <div className="w-18 h-13 rounded-xl bg-white border border-emerald-300 shadow-md p-1.5 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
                        <ScanFace className="w-5 h-5" />
                      </div>
                      <div className="space-y-1">
                        <div className="w-6 h-1.5 rounded-full bg-emerald-400" />
                        <div className="w-4 h-1 rounded-full bg-emerald-200" />
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="text-base font-bold text-neutral-900 group-hover:text-emerald-600 transition-colors">
                  Project Management & HRMS
                </h3>
                <div className="text-[10px] font-mono text-neutral-500 font-semibold mt-0.5 mb-2">
                  Agile Projects + Sprints + BioAuth + RBAC
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                  Agile sprint tracking, task kanban boards, in-house facial biometric attendance, and PIN-secured meetings.
                </p>

                {/* 4 Bullet Points */}
                <div className="space-y-2 text-xs text-neutral-700 pl-0.5 border-t border-black/5 pt-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Agile Project & Sprint Management</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">Task Kanban Boards & Milestones</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">AI Face Biometric Attendance</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-[11px] font-medium text-neutral-700">MeetPass Video Meetings & RBAC</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => openConsultationModal("Inspect HRMS BioAuth Engine")}
                className="w-full mt-5 py-2.5 rounded-full bg-white hover:bg-neutral-50 text-neutral-800 text-xs font-semibold border border-black/10 shadow-2xs flex items-center justify-center gap-1.5 transition-all group-hover:border-emerald-400"
              >
                <span>Inspect Engine</span>
                <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 06 — AUTONOMOUS WORKFLOWS                                              */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="workflows" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-white border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-100 border border-black/5 text-[11px] font-mono font-semibold text-neutral-700 uppercase mb-4">
              <Zap className="w-3.5 h-3.5 text-[#FF5A1F]" />
              AUTONOMOUS WORKFLOWS
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#0B0B0B] mb-4 tracking-tight">
              From Event to Business Outcome. Automatically.
            </h2>
            <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
              nORBIT eliminates manual handoffs between separate tools. Explore real autonomous cross-engine workflows running across sales, client support, talent verification, server defense, and workforce payroll.
            </p>
          </div>

          {/* Workflow Selector Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {workflows.map((wf) => (
              <button
                key={wf.id}
                onClick={() => setActiveWorkflow(wf.id)}
                className={`px-4 sm:px-5 py-2.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                  activeWorkflow === wf.id
                    ? "bg-[#FF5A1F] text-white shadow-md shadow-[#FF5A1F]/25 scale-105"
                    : "bg-[#FAF9F6] text-neutral-600 hover:text-neutral-900 border border-black/10 hover:border-black/20"
                }`}
              >
                {wf.name}
              </button>
            ))}
          </div>

          {/* Active Workflow Card */}
          {(() => {
            const currentWf = workflows.find((w) => w.id === activeWorkflow) || workflows[0];
            return (
              <div className="p-6 sm:p-8 rounded-3xl bg-[#FAF9F6] border border-black/10 shadow-sm text-left">
                {/* Workflow Trigger Banner */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-black/5 mb-6">
                  <div>
                    <span className="text-xs font-mono font-bold text-neutral-500 uppercase tracking-wider">
                      Active Event Cascade
                    </span>
                    <h3 className="text-lg font-bold text-neutral-900 mt-0.5">{currentWf.tagline}</h3>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white border border-black/10 text-xs font-mono text-neutral-700">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>Trigger: <strong>{currentWf.trigger}</strong></span>
                  </div>
                </div>

                {/* 5-Stage Animated Workflow Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3.5 text-center">
                  {currentWf.stages.map((stage, sIdx) => {
                    const StageIcon = stage.icon;
                    return (
                      <div
                        key={sIdx}
                        className="p-4 rounded-2xl bg-white border border-black/10 hover:border-[#FF5A1F]/50 hover:shadow-md transition-all relative group flex flex-col justify-between"
                      >
                        <div>
                          <div className={`w-9 h-9 rounded-xl ${stage.bg} grid place-items-center mx-auto mb-2 group-hover:scale-110 transition-transform`}>
                            <StageIcon className={`w-4 h-4 ${stage.color}`} />
                          </div>
                          <div className="text-xs font-bold text-neutral-900 mb-0.5">{stage.label}</div>
                          <div className="text-[10px] font-mono font-semibold text-[#FF5A1F] mb-1.5">{stage.engine}</div>
                          <p className="text-[11px] text-neutral-500 leading-snug">{stage.desc}</p>
                        </div>
                        <div className="pt-3 border-t border-black/5 mt-3 text-[9px] font-mono text-neutral-400">
                          STAGE 0{sIdx + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </section>



      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 08 — SAAS STACK DISRUPTION (NO THIRD PARTY LOGOS)                      */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="savings" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-white border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-100 border border-black/5 text-[11px] font-mono font-semibold text-neutral-700 uppercase mb-4">
              <TrendingUp className="w-3.5 h-3.5 text-[#FF5A1F]" />
              THE SAAS STACK DISRUPTION
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#0B0B0B] mb-4 tracking-tight">
              The SaaS Stack Is Fragmented. nORBIT Brings It In-House.
            </h2>
            <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
              Replacing 7+ fragmented external SaaS subscriptions with nORBIT's sovereign in-house engines saves significant recurring budget while keeping customer data strictly in your possession.
            </p>
          </div>

          {/* Cost Comparison Hero Strip */}
          <div className="mb-12 p-6 sm:p-8 rounded-3xl bg-[#FAF9F6] border border-black/10 text-center">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="p-4 rounded-2xl bg-white border border-red-200/70 text-left">
                <div className="text-xs font-mono font-bold text-red-600 uppercase">Fragmented 7+ SaaS Tools</div>
                <div className="text-2xl sm:text-3xl font-display font-bold text-neutral-900 mt-1">
                  ₹1,85,000 – ₹3,60,000
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">Per Month (Avg. 40-member tech company)</div>
              </div>

              <div className="text-center">
                <div className="inline-block px-4 py-2 rounded-full bg-[#0B0B0B] text-white font-mono text-xs font-bold shadow-md">
                  DISRUPTED BY nORBIT
                </div>
                <div className="text-xs text-neutral-500 mt-2 font-mono">100% In-House Sovereign Platform</div>
              </div>

              <div className="p-4 rounded-2xl bg-white border border-emerald-300 text-left">
                <div className="text-xs font-mono font-bold text-emerald-600 uppercase">nORBIT In-House Operating OS</div>
                <div className="text-2xl sm:text-3xl font-display font-bold text-emerald-600 mt-1">
                  ₹0 Extra SaaS Cost
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">Runs on your own sovereign VPS nodes</div>
              </div>
            </div>
          </div>

          {/* Category-by-Category Table / Cards (Zero 3rd party logos) */}
          <div className="space-y-3">
            {saasDisruptions.map((item, idx) => (
              <div
                key={idx}
                className="p-4 sm:p-5 rounded-2xl bg-[#FAF9F6] border border-black/5 hover:border-black/15 transition-all grid md:grid-cols-12 gap-4 items-center text-left"
              >
                <div className="md:col-span-3">
                  <div className="text-xs font-mono text-neutral-400">CATEGORY 0{idx + 1}</div>
                  <div className="text-sm font-bold text-neutral-900 mt-0.5">{item.category}</div>
                </div>

                <div className="md:col-span-3">
                  <div className="text-xs text-neutral-500">{item.fragmented}</div>
                  <div className="text-xs font-mono text-red-600 font-semibold mt-1">{item.costRange}</div>
                </div>

                <div className="md:col-span-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 md:pt-0 border-t md:border-t-0 border-black/5">
                  <div>
                    <div className="text-xs font-bold text-[#FF5A1F] flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{item.norbitEngine}</span>
                    </div>
                    <div className="text-xs text-neutral-600 mt-0.5">{item.inHouseBenefit}</div>
                  </div>
                  <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200 shrink-0 font-bold">
                    IN-HOUSE
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 09 — DEPLOYMENT ON YOUR TERMS                                          */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section id="deployments" className="scroll-mt-24 py-20 sm:py-24 px-5 sm:px-8 bg-[#FAF9F6] border-b border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-black/10 text-[11px] font-mono font-semibold text-neutral-700 uppercase mb-4 shadow-xs">
              <Server className="w-3.5 h-3.5 text-[#FF5A1F]" />
              DEPLOYMENT & DATA SOVEREIGNTY
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#0B0B0B] mb-4 tracking-tight">
              Deploy on Your Terms.
            </h2>
            <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
              100% data sovereignty. Run nORBIT on high-performance dedicated bare-metal, private virtual cloud, or fully air-gapped institutional environments.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 rounded-3xl bg-white border border-black/10 hover:border-black/20 shadow-sm text-left">
              <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center mb-4">
                <HardDrive className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">Dedicated Bare-Metal</h3>
              <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                Maximum computational throughput with AMD EPYC/NVMe hardware and raw kernel access for Guardian SRE operations.
              </p>
              <div className="text-[11px] font-mono text-neutral-500 border-t border-black/5 pt-3">
                Ideal for: High-traffic SaaS & Tech Agencies
              </div>
            </div>

            <div className="p-6 rounded-3xl bg-white border border-black/10 hover:border-black/20 shadow-sm text-left">
              <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 grid place-items-center mb-4">
                <Cloud className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">Private Cloud (VPC)</h3>
              <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                Isolated, encrypted environment configured inside your private virtual cloud with custom firewall rules and BYOVPS support.
              </p>
              <div className="text-[11px] font-mono text-neutral-500 border-t border-black/5 pt-3">
                Ideal for: Fast-growing Enterprises & Scaleups
              </div>
            </div>

            <div className="p-6 rounded-3xl bg-white border border-black/10 hover:border-black/20 shadow-sm text-left">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center mb-4">
                <Lock className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 mb-2">Air-Gapped On-Premises</h3>
              <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                Zero external egress network connections for defense, banking, and sensitive regulated government institutions.
              </p>
              <div className="text-[11px] font-mono text-neutral-500 border-t border-black/5 pt-3">
                Ideal for: Regulated Banking & Defense Compliance
              </div>
            </div>
          </div>

          {/* Turnkey Migration Banner */}
          <div className="p-6 sm:p-8 rounded-3xl bg-white border border-black/10 shadow-sm text-left flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <span className="text-xs font-mono font-bold text-[#FF5A1F] uppercase tracking-wider">
                WHITE-GLOVE MIGRATION
              </span>
              <h3 className="text-xl font-bold text-neutral-900 mt-1 mb-1">
                Migrate From Fragmented Tools with Zero Downtime
              </h3>
              <p className="text-xs sm:text-sm text-neutral-600 max-w-2xl leading-relaxed">
                Our engineering team ports your CRM leads, client invoices, historical accounting ledgers, and employee databases with automated parity audits.
              </p>
            </div>
            <button
              onClick={() => openConsultationModal("Schedule Migration Review")}
              className="px-6 py-3.5 rounded-full bg-[#0B0B0B] hover:bg-neutral-800 text-white font-semibold text-xs transition-all shrink-0 flex items-center gap-2 shadow-md"
            >
              <span>Schedule Migration Review</span>
              <ArrowRight className="w-3.5 h-3.5 text-[#FF5A1F]" />
            </button>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 10 — FINAL CTA                                                         */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-gradient-to-b from-white to-[#FAF9F6] text-center">
        <div className="max-w-4xl mx-auto">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-neutral-100 border border-black/10 text-[11px] font-mono font-semibold text-neutral-800 uppercase mb-6">
            <span className="w-2 h-2 rounded-full bg-[#FF5A1F] animate-pulse" />
            READY TO UNIFY YOUR BUSINESS?
          </div>

          <h2 className="text-3xl sm:text-5xl font-display font-bold text-[#0B0B0B] mb-5 tracking-tight">
            See nORBIT in Action.
          </h2>

          <p className="text-neutral-600 text-sm sm:text-base max-w-2xl mx-auto mb-10 leading-relaxed">
            Discover how nORBIT can unify your infrastructure, client portal, billing, AI talent verification, and workforce on one sovereign platform.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
            <button
              onClick={() => openConsultationModal("Book a Free Consultation")}
              className="px-8 py-4 rounded-full bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-semibold text-sm transition-all duration-300 shadow-xl shadow-[#FF5A1F]/25 flex items-center gap-2"
            >
              <span>Book a Free Consultation</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => openConsultationModal("Talk to Sales")}
              className="px-8 py-4 rounded-full bg-white hover:bg-neutral-100 text-neutral-900 font-semibold text-sm transition-all duration-300 border border-black/10 shadow-xs"
            >
              Talk to Sales
            </button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs font-mono text-neutral-500 pt-8 border-t border-black/5">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Built in India for Global Impact</span>
            </div>
            <span className="hidden sm:inline text-neutral-300">•</span>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-600" />
              <span>India DPDP Act 2023 Compliant</span>
            </div>
            <span className="hidden sm:inline text-neutral-300">•</span>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FF5A1F]" />
              <span>Startups to Regulated Enterprises</span>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
